<template>
  <b-modal
    class="dark-backdrop fullscreen-image-carousel"
    v-bind="modalProps"
    modal-class="fullscreen-image-carousel-modal overflow-hidden"
    dialog-class="!static"
    content-class="!static"
    body-class="!static"
    transition=""
    size="xl"
    :hide-footer="true"
    :hide-header="true"
    no-close-on-backdrop
  >
    <ModalCloseButton
      class="pt-2 pr-2"
      shadow
      align="inside"
      @click="onHide"
    />
    <client-only>
      <ScreenWidthProvider
        v-if="images.length"
        v-slot="{ isWider }"
        class="flex flex-col justify-between items-center"
      >
        <Swiper
          class="fullscreen-image-carousel-main-swiper size-full pt-12 md:pt-0"
          v-bind="mainSliderOptions"
          :initial-slide="initialSlide"
          :thumbs="$refs.thumbnails"
          uses-thumbs
        >
          <div
            v-for="(imgUrl, index) in imagesFullSize"
            :key="`image_${index}`"
            class="fullscreen-image-carousel-main-slide flex justify-center items-center w-full"
          >
            <ResponsiveHotelImage
              :key="`image_${index}`"
              class="fullscreen-image-carousel-main-slide-image"
              :image="imgUrl"
              preferred-size="max"
              :alt="`Slide ${index}`"
              fit="scale-down"
              thumb-fit="contain"
              :sizing-ref="$refs.main"
              clip="height"
            />
          </div>
          <template #prevArrowContainer>
            <div
              class="swiper-carousel-arrow-prev left-0"
              :class="arrowClasses"
            >
              <fa icon="chevron-left" />
            </div>
          </template>
          <template #nextArrowContainer>
            <div
              class="swiper-carousel-arrow-next right-0"
              :class="arrowClasses"
            >
              <fa icon="chevron-right" />
            </div>
          </template>
        </Swiper>
        <Swiper
          v-show="images.length > 1 && isWider('md')"
          ref="thumbnails"
          class="fullscreen-image-carousel-thumbnails-swiper fixed bottom-0 left-0 w-full py-4 z-[1]"
          v-bind="thumbSliderOptions"
          :initial-slide="initialSlide"
          uses-thumbs
        >
          <div
            v-for="(image, index) in imageSelection"
            :key="`thumb_${index}`"
            class="flex items-center cursor-pointer h-full"
          >
            <ResponsiveHotelImage
              :image="image"
              :preferred-size="isWider('lg') ? 'large' : 'thumbnail'"
              :alt="`Slide thumb ${index}`"
              fit="cover"
              aspect-ratio="4/3"
            />
          </div>
        </Swiper>
        <div class="safari-padding" />
      </ScreenWidthProvider>
    </client-only>
  </b-modal>
</template>

<script>
import Modal from '@packages/modals/Modal.vue'

export default defineNuxtComponent({
  props: {
    ...Modal.props,

    images: {
      type: Array,
      default: () => ([]),
    },

    initialSlide: {
      type: Number,
      default: 0,
    },
  },

  emits: ['onHide'],

  data () {
    return {
      mainSliderOptions: {
        pagination: false,
        navigation: true,
        rewind: true,
        onlyRenderVisibleSlides: true,
        preventSwiperUpdate: true,
        arrowsAlign: 'inside',
      },

      thumbSliderOptions: {
        verticalCenteredSlides: true,
        onlyRenderVisibleSlides: true,
        preventSwiperUpdate: true,
        spaceBetween: 20,
        pagination: false,
        navigation: false,
        rewind: true,
        isThumbs: true,

        slideToClickedSlide: true,
        breakpoints: {
          [getTailwindBreakpointsPx().md + 1]: {
            slidesPerView: this.images.length < 6 ? this.images.length : 6,
          },

          [getTailwindBreakpointsPx().xl]: {
            slidesPerView: this.images.length < 9 ? this.images.length : 9,
          },
        },
      },

      brokenImages: [],
    }
  },

  computed: {
    imageSelection () {
      return (this.images || [])
    },

    imagesFullSize () {
      return this.imageSelection
    },

    imagesThumbnails () {
      return this.imageSelection
    },

    modalProps () {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
      const { images, initialSlide, ...modalProps } = this.$props

      return modalProps
    },

    arrowClasses () {
      return 'swiper-carousel-arrow fixed top-0 flex items-center justify-center z-[2] !opacity-100'
    },
  },

  methods: {
    onHide () {
      this.$emit('onHide')
    },

  },
})
</script>

<style lang="scss">
$navigation-arrow-width: 52px;
$thumbnails-swiper-height: 100px;

/*! purgecss start ignore */
.fullscreen-image-carousel {
  &-modal {
    overflow: hidden;

    .modal-content {
      background: transparent;
      border: none;

      height: 100%;
    }

    .modal-backdrop {
      opacity: 0.5;
    }

    .modal-body {
      padding: 0;
    }
  }

  .swiper-carousel {
    &-arrow {
      font-size: 1.5rem;
      background: rgba(0, 0, 0, 0.5);
      padding: 0 1rem;
      color: #ccc;
      font-size: 24px;
      transition-duration: 0.3s;
      width: $navigation-arrow-width;
      height: calc(100vh - #{$thumbnails-swiper-height}) !important;

      @media (max-width: theme('screens.md')) {
        height: 100% !important;
      }

      &:hover {
        color: white;
      }
    }
  }

  &-main {
    &-slide {
      height: 85vh !important;

      @media (max-width: theme('screens.md')) {
        height: 86vh !important;
      }

      &-image {
        max-width: calc(100vw - (#{$navigation-arrow-width} * 2));
      }
    }
  }

  &-thumbnails {
    &-swiper {
      background: rgba(0, 0, 0, 0.5);
      height: $thumbnails-swiper-height;
    }

    &-slide {
      width: 130px;

      @at-root #{&} {
        &--fallback-image {
          background: none !important;
          max-width: 80px !important;

          & > img {
            width: 100%;
          }
        }
      }
    }
  }
}
/*! purgecss end ignore */
</style>
